// src/components/RegistrationAndLanding.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { ArrowRight, Check, ChevronRight, Mail, Shield, BarChart, Clock, Calendar, CreditCard, Users, Download, ExternalLink, Eye, FileText, Lock } from 'lucide-react';
import logger from '../utils/logger';

const RegistrationAndLanding = ({ pageType: initialPageType = 'landing' }) => {
  const [pageType, setPageType] = useState(initialPageType);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();
  const { instance } = useMsal();
  
  // Check for manual redirect flag and perform MSAL logout if necessary
  useEffect(() => {
    const handleManualRedirect = async () => {
      const manualRedirect = sessionStorage.getItem('manual_redirect');
      
      if (manualRedirect === 'true') {
        // Clear the flag
        sessionStorage.removeItem('manual_redirect');
        
        // Perform MSAL logout without redirect
        if (instance) {
          try {
            // Logout without redirect to avoid navigation loop
            await instance.logoutPopup({
              mainWindowRedirectUri: window.location.origin,
              onRedirectNavigate: () => false
            });
            logger.log('MSAL logout completed successfully');
          } catch (error) {
            logger.warn('Error during MSAL logout:', error);
          }
        }
      }
    };
    
    handleManualRedirect();
  }, [instance]);
  
  // Registration form state
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [bcTenantId, setBcTenantId] = useState('');
  const [bcEnvironment, setBcEnvironment] = useState('');
  const [bcCompanyName, setBcCompanyName] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  
  const handleRegistrationSubmit = () => {
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        // Registration complete
        alert('Registration successful! You will receive an email with further instructions.');
        navigate('/');
      }
    }, 1500);
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0 flex items-center">
                <svg className="h-8 w-8 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                <span className="ml-2 text-xl font-semibold tracking-tight text-gray-900">Business Central</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              {pageType === 'landing' && (
                <>
                  <Link to="/register" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-50 hover:bg-indigo-100">
                    Register
                  </Link>
                  <Link 
                    to="/login" 
                    onClick={() => {
                      localStorage.removeItem('clientCompanies');
                      localStorage.removeItem('clientCompanyId');
                      localStorage.removeItem('clientCompanyName');
                      localStorage.removeItem('employeeData');
                      localStorage.removeItem('employeeId');
                      localStorage.removeItem('employeeNo');
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Sign in
                  </Link>
                </>
              )}
              {pageType === 'registration' && (
                <button onClick={() => navigate('/')} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  Back to home
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Landing Page */}
      {pageType === 'landing' && (
        <div className="flex-1">
          {/* Hero Section */}
          <div className="bg-indigo-700 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
              <div className="lg:grid lg:grid-cols-12 lg:gap-8">
                <div className="lg:col-span-6">
                  <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                    Employee Portal for Business Central
                  </h1>
                  <p className="mt-6 text-xl leading-8">
                    Empower your employees with secure access to their payslips, personal data, and employment documents—all in one beautifully designed portal.
                  </p>
                  <div className="mt-8 flex space-x-4">
                    <Link to="/register" className="inline-flex items-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-gray-50">
                      Register your company
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                    <Link to="/pricing" className="inline-flex items-center px-5 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-indigo-800">
                      Pricing
                    </Link>
                  </div>
                </div>
                <div className="mt-12 lg:mt-0 lg:col-span-6">
                  <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center">
                        <div className="h-3 w-3 rounded-full bg-red-400 mr-2"></div>
                        <div className="h-3 w-3 rounded-full bg-yellow-400 mr-2"></div>
                        <div className="h-3 w-3 rounded-full bg-green-400"></div>
                      </div>
                    </div>
                    <div className="p-4">
                      <img 
                        src="/api/placeholder/600/400" 
                        alt="Employee Portal Preview" 
                        className="rounded-md shadow-md"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Features Section */}
          <div className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h2 className="text-3xl font-extrabold text-gray-900">
                  Everything your employees need in one place
                </h2>
                <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
                  A comprehensive self-service platform that simplifies HR operations and enhances employee experience.
                </p>
              </div>
              
              <div className="mt-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {/* Feature 1 */}
                  <div className="relative bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition duration-300">
                    <div>
                      <div className="absolute h-12 w-12 rounded-md bg-indigo-100 flex items-center justify-center">
                        <CreditCard className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div className="ml-16">
                        <h3 className="text-lg font-medium text-gray-900">Payslip Management</h3>
                        <p className="mt-2 text-base text-gray-500">
                          Secure access to current and historical payslips with detailed breakdowns of earnings and deductions.
                        </p>
                        <div className="mt-4 text-sm">
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Detailed earnings breakdown</p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">PDF download and print options</p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Historical payslip access</p>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Feature 2 */}
                  <div className="relative bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition duration-300">
                    <div>
                      <div className="absolute h-12 w-12 rounded-md bg-indigo-100 flex items-center justify-center">
                        <Users className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div className="ml-16">
                        <h3 className="text-lg font-medium text-gray-900">Employee Profiles</h3>
                        <p className="mt-2 text-base text-gray-500">
                          Comprehensive employee information with personal, employment, and payroll details in one view.
                        </p>
                        <div className="mt-4 text-sm">
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Personal information management</p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Tax and NI details</p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Employment history</p>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Feature 3 */}
                  <div className="relative bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition duration-300">
                    <div>
                      <div className="absolute h-12 w-12 rounded-md bg-indigo-100 flex items-center justify-center">
                        <FileText className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div className="ml-16">
                        <h3 className="text-lg font-medium text-gray-900">Document Management</h3>
                        <p className="mt-2 text-base text-gray-500">
                          Centralized repository for employment documents, contracts, and certificates.
                        </p>
                        <div className="mt-4 text-sm">
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Secure document storage</p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">P60s and tax documents</p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Easy download and sharing</p>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Feature 4 */}
                  <div className="relative bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition duration-300">
                    <div>
                      <div className="absolute h-12 w-12 rounded-md bg-indigo-100 flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div className="ml-16">
                        <h3 className="text-lg font-medium text-gray-900">Leave Management</h3>
                        <p className="mt-2 text-base text-gray-500">
                          Track and manage holiday, sick leave, and other absence types with ease.
                        </p>
                        <div className="mt-4 text-sm">
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Leave balance tracking</p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Request submission and approval</p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Leave history and calendar view</p>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Feature 5 */}
                  <div className="relative bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition duration-300">
                    <div>
                      <div className="absolute h-12 w-12 rounded-md bg-indigo-100 flex items-center justify-center">
                        <Clock className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div className="ml-16">
                        <h3 className="text-lg font-medium text-gray-900">Time Tracking</h3>
                        <p className="mt-2 text-base text-gray-500">
                          Monitor working hours, overtime, and time-off with comprehensive reporting.
                        </p>
                        <div className="mt-4 text-sm">
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Weekly timesheet summaries</p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Overtime calculation</p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Manager approval workflows</p>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Feature 6 */}
                  <div className="relative bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition duration-300">
                    <div>
                      <div className="absolute h-12 w-12 rounded-md bg-indigo-100 flex items-center justify-center">
                        <BarChart className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div className="ml-16">
                        <h3 className="text-lg font-medium text-gray-900">Reporting & Analytics</h3>
                        <p className="mt-2 text-base text-gray-500">
                          Gain insights with comprehensive reports on payroll, attendance, and more.
                        </p>
                        <div className="mt-4 text-sm">
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Earnings history visualization</p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Tax and deduction summaries</p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-green-500" />
                              </div>
                              <p className="ml-2 text-gray-600">Exportable financial data</p>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Benefits Section */}
          <div className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
                <div>
                  <h2 className="text-3xl font-extrabold text-gray-900">
                    Benefits for your organization
                  </h2>
                  <p className="mt-4 text-lg text-gray-500">
                    Our employee portal doesn't just benefit your staff—it transforms your entire HR operation with cost savings, increased efficiency, and improved compliance.
                  </p>
                  
                  <div className="mt-8 space-y-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-md bg-indigo-500 flex items-center justify-center">
                          <Shield className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-gray-900">Enhanced Security</h3>
                        <p className="mt-1 text-gray-500">
                          Microsoft 365 authentication and secure data handling ensure your employee data is protected at all times.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-md bg-indigo-500 flex items-center justify-center">
                          <Clock className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-gray-900">Time Savings</h3>
                        <p className="mt-1 text-gray-500">
                          Reduce HR administrative workload by up to 60% by empowering employees with self-service capabilities.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-md bg-indigo-500 flex items-center justify-center">
                          <BarChart className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-gray-900">Compliance Assurance</h3>
                        <p className="mt-1 text-gray-500">
                          Stay compliant with UK payroll and data protection regulations with automated record-keeping and secure access.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-10 lg:mt-0">
                  <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                    <div className="px-5 py-4 bg-indigo-600 text-white">
                      <h3 className="text-lg font-semibold">What our clients say</h3>
                    </div>
                    <div className="p-6">
                      <div className="space-y-6">
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                          <p className="italic text-gray-600">"The Business Central Employee Portal has transformed how our staff access their payroll information. Our HR team now spends 70% less time on administrative tasks."</p>
                          <div className="mt-4 flex items-center">
                            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-indigo-600 font-semibold">JM</span>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">Jane Mitchell</p>
                              <p className="text-sm text-gray-500">HR Director, Fusion Tech Ltd</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                          <p className="italic text-gray-600">"The integration with our existing Business Central setup was seamless. Our employees love having instant access to their payslips and documents."</p>
                          <div className="mt-4 flex items-center">
                            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-indigo-600 font-semibold">RK</span>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">Robert Kumar</p>
                              <p className="text-sm text-gray-500">CFO, Northbridge Partners</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6">
                        <Link to="/register" className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
                          Get started today
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* CTA Section */}
          <div className="bg-indigo-700">
            <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
              <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                <span className="block">Ready to transform your employee experience?</span>
                <span className="block text-indigo-200">Register your Business Central today.</span>
              </h2>
              <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
                <div className="inline-flex rounded-md shadow">
                  <Link to="/register" className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50">
                    Register now
                  </Link>
                </div>
                <div className="ml-3 inline-flex rounded-md shadow">
                  <a href="#" className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-800 hover:bg-indigo-900">
                    Contact sales
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Registration Page */}
      {pageType === 'registration' && (
        <div className="flex-1 py-12">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-3xl font-extrabold text-gray-900">Register your Business Central</h1>
              <p className="mt-4 text-lg text-gray-500">
                Connect your Business Central environment to provide employees with secure access to their data
              </p>
            </div>
            
            {/* Steps */}
            <div className="mb-12">
              <nav aria-label="Progress">
                <ol className="flex items-center justify-center">
                  <li className={`relative pr-8 sm:pr-20 ${currentStep >= 1 ? 'text-indigo-600' : 'text-gray-400'}`}>
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                      <div className={`h-0.5 w-full ${currentStep > 1 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
                    </div>
                    <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ${
                      currentStep > 1 ? 'bg-indigo-600' : currentStep === 1 ? 'border-2 border-indigo-600 bg-white' : 'border-2 border-gray-300 bg-white'
                    }`}>
                      {currentStep > 1 ? (
                        <Check className={`h-5 w-5 text-white`} />
                      ) : (
                        <span className={`${currentStep === 1 ? 'text-indigo-600' : 'text-gray-500'}`}>1</span>
                      )}
                    </div>
                    <div className="absolute top-10 text-center w-32 -left-12 text-sm font-medium">
                      Company Details
                    </div>
                  </li>
                  
                  <li className={`relative pr-8 sm:pr-20 ${currentStep >= 2 ? 'text-indigo-600' : 'text-gray-400'}`}>
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                      <div className={`h-0.5 w-full ${currentStep > 2 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
                    </div>
                    <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ${
                      currentStep > 2 ? 'bg-indigo-600' : currentStep === 2 ? 'border-2 border-indigo-600 bg-white' : 'border-2 border-gray-300 bg-white'
                    }`}>
                      {currentStep > 2 ? (
                        <Check className={`h-5 w-5 text-white`} />
                      ) : (
                        <span className={`${currentStep === 2 ? 'text-indigo-600' : 'text-gray-500'}`}>2</span>
                      )}
                    </div>
                    <div className="absolute top-10 text-center w-32 -left-12 text-sm font-medium">
                      BC Configuration
                    </div>
                  </li>
                  
                  <li className={`relative ${currentStep >= 3 ? 'text-indigo-600' : 'text-gray-400'}`}>
                    <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ${
                      currentStep > 3 ? 'bg-indigo-600' : currentStep === 3 ? 'border-2 border-indigo-600 bg-white' : 'border-2 border-gray-300 bg-white'
                    }`}>
                      {currentStep > 3 ? (
                        <Check className={`h-5 w-5 text-white`} />
                      ) : (
                        <span className={`${currentStep === 3 ? 'text-indigo-600' : 'text-gray-500'}`}>3</span>
                      )}
                    </div>
                    <div className="absolute top-10 text-center w-32 -left-12 text-sm font-medium">
                      Confirmation
                    </div>
                  </li>
                </ol>
              </nav>
            </div>
            
            <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
              {/* Step 1: Company Details */}
              {currentStep === 1 && (
                <div>
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-medium text-gray-900">Company Details</h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Let's get to know your organization
                    </p>
                  </div>
                  <div className="px-6 py-6 space-y-6">
                    <div>
                      <label htmlFor="company-name" className="block text-sm font-medium text-gray-700">
                        Company Name
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          id="company-name"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="Acme Corporation Ltd"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="company-email" className="block text-sm font-medium text-gray-700">
                        Company Email
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="email"
                          id="company-email"
                          value={companyEmail}
                          onChange={(e) => setCompanyEmail(e.target.value)}
                          className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="admin@company.com"
                        />
                      </div>
                      <p className="mt-2 text-sm text-gray-500">We'll send confirmation details to this email address</p>
                    </div>
                    
                    <div className="pt-4">
                      <div className="flex justify-end">
                        <button
                          onClick={handleRegistrationSubmit}
                          disabled={!companyName || !companyEmail || isLoading}
                          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                            !companyName || !companyEmail || isLoading 
                              ? 'bg-indigo-300 cursor-not-allowed' 
                              : 'bg-indigo-600 hover:bg-indigo-700'
                          }`}
                        >
                          {isLoading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            <>
                              Continue
                              <ChevronRight className="ml-2 h-4 w-4" />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Step 2: Business Central Configuration */}
              {currentStep === 2 && (
                <div>
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-medium text-gray-900">Business Central Configuration</h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Connect your Business Central environment
                    </p>
                  </div>
                  <div className="px-6 py-6 space-y-6">
                    <div>
                      <label htmlFor="bc-tenant-id" className="block text-sm font-medium text-gray-700">
                        Business Central Tenant ID
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          id="bc-tenant-id"
                          value={bcTenantId}
                          onChange={(e) => setBcTenantId(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        />
                      </div>
                      <p className="mt-2 text-sm text-gray-500">Your unique Business Central tenant identifier</p>
                    </div>
                    
                    <div>
                      <label htmlFor="bc-environment" className="block text-sm font-medium text-gray-700">
                        Environment Name
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          id="bc-environment"
                          value={bcEnvironment}
                          onChange={(e) => setBcEnvironment(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="Production"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="bc-company-name" className="block text-sm font-medium text-gray-700">
                        Company Name
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          id="bc-company-name"
                          value={bcCompanyName}
                          onChange={(e) => setBcCompanyName(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="CRONUS UK Ltd."
                        />
                      </div>
                      <p className="mt-2 text-sm text-gray-500">The company name within your Business Central environment</p>
                    </div>
                    
                    <div className="pt-4">
                      <div className="flex justify-between">
                        <button
                          onClick={() => setCurrentStep(1)}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Back
                        </button>
                        <button
                          onClick={handleRegistrationSubmit}
                          disabled={!bcTenantId || !bcEnvironment || !bcCompanyName || isLoading}
                          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                            !bcTenantId || !bcEnvironment || !bcCompanyName || isLoading 
                              ? 'bg-indigo-300 cursor-not-allowed' 
                              : 'bg-indigo-600 hover:bg-indigo-700'
                          }`}
                        >
                          {isLoading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            <>
                              Continue
                              <ChevronRight className="ml-2 h-4 w-4" />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Step 3: Confirmation */}
              {currentStep === 3 && (
                <div>
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-medium text-gray-900">Confirmation</h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Review and confirm your details
                    </p>
                  </div>
                  <div className="px-6 py-6">
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h3 className="text-sm font-medium text-gray-900 uppercase tracking-wider mb-3">Company Details</h3>
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                        <div className="sm:col-span-1">
                          <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                          <dd className="mt-1 text-sm text-gray-900">{companyName}</dd>
                        </div>
                        <div className="sm:col-span-1">
                          <dt className="text-sm font-medium text-gray-500">Company Email</dt>
                          <dd className="mt-1 text-sm text-gray-900">{companyEmail}</dd>
                        </div>
                      </dl>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h3 className="text-sm font-medium text-gray-900 uppercase tracking-wider mb-3">Business Central Configuration</h3>
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                        <div className="sm:col-span-1">
                          <dt className="text-sm font-medium text-gray-500">Tenant ID</dt>
                          <dd className="mt-1 text-sm text-gray-900">{bcTenantId}</dd>
                        </div>
                        <div className="sm:col-span-1">
                          <dt className="text-sm font-medium text-gray-500">Environment</dt>
                          <dd className="mt-1 text-sm text-gray-900">{bcEnvironment}</dd>
                        </div>
                        <div className="sm:col-span-2">
                          <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                          <dd className="mt-1 text-sm text-gray-900">{bcCompanyName}</dd>
                        </div>
                      </dl>
                    </div>
                    
                    <div className="rounded-md bg-blue-50 p-4 mb-6">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3 flex-1 md:flex md:justify-between">
                          <p className="text-sm text-blue-700">
                            After registration, you will receive an email with instructions on how to complete the setup.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center mb-6">
                      <input
                        id="terms"
                        name="terms"
                        type="checkbox"
                        checked={agreeTerms}
                        onChange={(e) => setAgreeTerms(e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
                        I agree to the <a href="#" className="text-indigo-600 hover:text-indigo-500">Terms of Service</a> and <a href="#" className="text-indigo-600 hover:text-indigo-500">Privacy Policy</a>
                      </label>
                    </div>
                    
                    <div className="flex justify-between">
                      <button
                        onClick={() => setCurrentStep(2)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleRegistrationSubmit}
                        disabled={!agreeTerms || isLoading}
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                          !agreeTerms || isLoading 
                            ? 'bg-indigo-300 cursor-not-allowed' 
                            : 'bg-indigo-600 hover:bg-indigo-700'
                        }`}
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                          </>
                        ) : (
                          'Complete Registration'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
          <div className="xl:grid xl:grid-cols-3 xl:gap-8">
            <div className="grid grid-cols-2 gap-8 xl:col-span-2">
              <div className="md:grid md:grid-cols-2 md:gap-8">
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Solutions</h3>
                  <ul className="mt-4 space-y-4">
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Payroll Management
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Employee Self-Service
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Document Management
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        HR Analytics
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="mt-12 md:mt-0">
                  <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Support</h3>
                  <ul className="mt-4 space-y-4">
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Documentation
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        API Reference
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Guides
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Help Center
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="md:grid md:grid-cols-2 md:gap-8">
                <div>
                  <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Company</h3>
                  <ul className="mt-4 space-y-4">
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        About
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Blog
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Careers
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Contact
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="mt-12 md:mt-0">
                  <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Legal</h3>
                  <ul className="mt-4 space-y-4">
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Privacy
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Terms
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        Cookie Policy
                      </a>
                    </li>
                    <li>
                      <a href="#" className="text-base text-gray-500 hover:text-gray-900">
                        GDPR
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="mt-8 xl:mt-0">
              <div className="flex items-center">
                <svg className="h-10 w-10 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                <h3 className="ml-2 text-xl font-semibold text-gray-900">Business Central</h3>
              </div>
              <p className="mt-4 text-base text-gray-500">
                The premier employee portal solution for Business Central users. Simplify HR operations and enhance employee experience.
              </p>
              <div className="mt-4">
                <p className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Subscribe to our newsletter</p>
                <div className="mt-2 flex">
                  <input
                    aria-label="Email address"
                    type="email"
                    required
                    className="appearance-none min-w-0 w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-4 text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:placeholder-gray-400"
                    placeholder="Enter your email"
                  />
                  <div className="ml-3 rounded-md shadow">
                    <button className="w-full flex items-center justify-center py-2 px-4 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
                      Subscribe
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 border-t border-gray-200 pt-8">
            <p className="text-base text-gray-400 xl:text-center">
              &copy; 2025 SiriusConnect365. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RegistrationAndLanding;